<template>
  <v-list subheader class="invoice-data-list">
    <template v-if="!hiddenSections.includes('booking')">
      <template v-if="isLoading">
        <div
          aria-busy="true"
          aria-live="polite"
          role="alert"
          class="v-skeleton-loader v-skeleton-loader--is-loading theme--light"
          style="max-width: 300px; height: 62px;"
        >
          <div class="v-skeleton-loader__list-item-avatar v-skeleton-loader__bone" style="margin-top: 7px;">
            <div class="v-skeleton-loader__avatar v-skeleton-loader__bone" style="width: 30px; height: 30px; margin-left: -3px;"></div>
            <div style="margin-top: -8px; width: 160px; max-width: 160px; margin-left: 12px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
            <div style="position: absolute; left: 72px; top: 32px; width: 212px; height: 8px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
          </div>
        </div>
      </template>
      <template v-else-if="invoice.booking || invoice.booking_group">
        <v-list-item @click.native="openBooking" :ripple="bookingClickable" :class="{ 'cursor-pointer highlight-on-hover': bookingClickable }">
          <v-list-item-icon style="margin-right: 29px;">
            <v-list-item-avatar
              :color="invoice.booking ? 'deep-purple' : 'pink'"
              class="justify-center mr-0 my-0"
              size="30"
              style="margin-left: -3px;"
            >
              <v-icon color="white" small>
                {{ invoice.booking ? 'mdi-book-marker-outline' : 'mdi-calendar-multiple' }}
              </v-icon>
            </v-list-item-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              <span>{{ invoice.booking ? invoice.booking.booking_number : invoice.booking_group.booking_number }}</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-show="hasBookingRemarks"
                    aria-hidden="false"
                    class="ml-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-tag-text-outline
                  </v-icon>
                </template>
                <div style="padding: 4px;">
                  <h3>Bokningsanteckningar:</h3>
                  <i>{{ invoice.booking ? invoice.booking.remarks : invoice.booking_group.remarks }}</i>
                </div>
              </v-tooltip>
            </v-list-item-title>
            <v-list-item-subtitle style="width: auto;">{{ invoice.booking ? 'Bokningsnummer' : 'Bokningsnummer gruppbokning' }}</v-list-item-subtitle>
          </v-list-item-content>

        </v-list-item>
      </template>
    </template>

    <template v-if="!hiddenSections.includes('customer')">
      <template v-if="isLoading">
        <div
          aria-busy="true"
          aria-live="polite"
          role="alert"
          class="v-skeleton-loader v-skeleton-loader--is-loading theme--light"
          style="max-width: 300px; height: 62px;"
        >
          <div class="v-skeleton-loader__list-item-avatar v-skeleton-loader__bone" style="margin-top: 7px;">
            <div class="v-skeleton-loader__avatar v-skeleton-loader__bone" style="width: 30px; height: 30px; margin-left: -3px;"></div>
            <div style="margin-top: -8px; width: 160px; max-width: 160px; margin-left: 12px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
            <div style="position: absolute; left: 72px; top: 32px; width: 212px; height: 8px;" class="v-skeleton-loader__text v-skeleton-loader__bone"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <v-list-item @click.native="openCustomer" :ripple="customerClickable" :class="{ 'cursor-pointer highlight-on-hover': customerClickable }">
          <v-list-item-icon style="margin-right: 29px;">
            <customer-avatar :customer="invoice.customer" style="margin-left: -3px;"></customer-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title v-text="invoice.customer.full_name"></v-list-item-title>
            <v-list-item-subtitle v-text="invoice.customer.shortAddress"></v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </template>

    <template v-if="!hiddenSections.includes('invoice-type')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>mdi-file-document-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              {{ invoice.invoice_type_text }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Fakturatyp</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('invoice-date')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>coi-invoice-date</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              {{ invoiceDateFormatted }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Fakturadatum</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('due-date')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>coi-due-date</v-icon>
        </v-list-item-icon>
        <v-list-item-content class="flex-shrink-1 flex-grow-0 d-block" style="flex: unset;">
          <v-list-item-title style="">
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              <div v-html="dueDateFormatted"></div>
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Förfallodatum</v-list-item-subtitle>
        </v-list-item-content>
        <div v-if="invoice?.isOverdue" class="flex-grow-1">
          <v-chip
            class="ml-6"
            small
            color="error"
            text-color="white"
          >
            Förfallen
          </v-chip>
        </div>
        <div v-else-if="invoice?.isOverdueDeposit" class="flex-grow-1">
          <v-chip
            class="ml-6"
            small
            color="error"
            text-color="white"
          >
            Förfallen handpenning
          </v-chip>
        </div>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('amount')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>mdi-cash</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              <div v-html="invoiceAmountFormatted"></div>
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Fakturabelopp</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('remaining-amount')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>mdi-hand-extended</v-icon>
        </v-list-item-icon>
        <v-list-item-content class="flex-shrink-1 flex-grow-0 d-block" style="flex: unset;">
          <v-list-item-title style="">
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              {{ remainingAmountFormatted }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Återstående belopp</v-list-item-subtitle>
        </v-list-item-content>
        <div v-if="isClosed" class="flex-grow-1">
          <v-chip
            class="ml-6"
            small
            color="success"
            text-color="white"
          >
            Fullbetald
          </v-chip>
        </div>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('original-invoice') && hasOriginalInvoice">
      <v-list-item @click.native="openOriginalInvoice" ripple class="cursor-pointer highlight-on-hover">
        <v-list-item-icon class="my-auto">
          <v-icon>mdi-file-document</v-icon>
        </v-list-item-icon>
        <v-list-item-content class="flex-shrink-1 flex-grow-0 d-block" style="flex: unset;">
          <v-list-item-title style="">
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              {{ originalInvoiceText }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Krediterad faktura</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('payment-term')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>mdi-cash-clock</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              {{ invoice.paymentTerm.name }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>Betalvillkor</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-if="!hiddenSections.includes('ocr')">
      <v-list-item>
        <v-list-item-icon class="my-auto">
          <v-icon>mdi-ocr</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            <template v-if="isLoading">
              <v-skeleton-loader
                type="list-item"
                style="max-width: 200px;"
                class="list-item-has-subtitle"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              {{ invoice.ocr }}
            </template>
          </v-list-item-title>
          <v-list-item-subtitle>OCR nummer</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

  </v-list>
</template>

<script>
import CustomerAvatar from '@/components/customer/Avatar.vue'

export default {
  name: 'InvoiceDataList',
  components: {
    CustomerAvatar
  },
  props: {
    invoice: Object,
    isLoading: Boolean,
    customerClickable: Boolean,
    bookingClickable: Boolean,
    hiddenSections: {
      type: Array,
      default: function () { return [] }
    }
  },
  data: () => ({}),
  computed: {
    invoiceDateFormatted: function () {
      if (!this.invoice) {
        return ''
      }
      const formattedDate = this.invoice.invoice_date.format('YYYY-MM-DD')
      return `${formattedDate}`
    },
    dueDateFormatted: function () {
      if (!this.invoice) {
        return ''
      }
      const formattedDueDate = this.invoice.due_date.format('YYYY-MM-DD')
      if (this.invoice.deposit_due_date) {
        const formattedDepositDueDate = this.invoice.deposit_due_date.format('YYYY-MM-DD')
        return `${formattedDueDate} <span style="font-size: 80%;">(handpenning: ${formattedDepositDueDate})</span>`
      }
      return formattedDueDate
    },
    invoiceAmountFormatted: function () {
      if (!this.invoice) {
        return ''
      }
      const formattedAmount = window.conversion.toHumanReadableAmount(this.invoice.total_amount / 100)
      if (this.invoice.deposit_amount) {
        const formattedDepositAmount = window.conversion.toHumanReadableAmount(this.invoice.deposit_amount / 100)
        return `${formattedAmount} <span style="font-size: 80%;">(varav handpenning: ${formattedDepositAmount})</span>`
      }
      return formattedAmount
    },
    remainingAmountFormatted: function () {
      if (!this.invoice) {
        return ''
      }
      const formattedAmount = window.conversion.toHumanReadableAmount(this.invoice.remaining_amount / 100)
      return formattedAmount
    },
    isClosed: function () {
      return this.invoice && this.invoice.status === 'closed'
    },
    hasOriginalInvoice: function () {
      return this.invoice && this.invoice.original_invoice_id
    },
    originalInvoiceText: function () {
      if (!this.hasOriginalInvoice) {
        return ''
      }
      return `Faktura ${this.invoice.original_invoice_id}`
    },
    hasBookingRemarks: function () {
      return (this.invoice?.booking && !!this.invoice.booking.remarks) || (this.invoice?.booking_group && !!this.invoice.booking_group.remarks)
    }
  },
  methods: {
    openCustomer: function () {
      if (this.customerClickable && this.invoice.customer_id) {
        this.$store.commit('customer/SET_SHOW_DIALOG', false) // stänger dialog först, om dialogen redan skulle vara öppen i bakgrunden så flyttas den inte till förgrunden annars
        this.$nextTick(() => {
          this.$store.dispatch('customer/openDialogById', { customerId: this.invoice.customer_id })
        })
      }
    },
    openBooking: function () {
      if (this.bookingClickable && this.invoice.booking_id) {
        this.$store.commit('booking/SET_SHOW_DIALOG', false) // stänger dialog först, om dialogen redan skulle vara öppen i bakgrunden så flyttas den inte till förgrunden annars
        this.$nextTick(() => {
          this.$store.dispatch('booking/openDialogById', { bookingId: this.invoice.booking_id })
        })
      } else if (this.bookingClickable && this.invoice.booking_group_id) {
        this.$store.commit('bookingGroup/SET_SHOW_DIALOG', false) // stänger dialog först, om dialogen redan skulle vara öppen i bakgrunden så flyttas den inte till förgrunden annars
        this.$nextTick(() => {
          this.$store.dispatch('bookingGroup/openDialogById', { bookingGroupId: this.invoice.booking_group_id })
        })
      }
    },
    openOriginalInvoice: function () {
      if (!this.hasOriginalInvoice) {
        return false
      }
      this.$store.dispatch('invoice/openDialogById', { invoiceId: this.invoice.original_invoice_id })
    },
    close: function () {
      this.$emit('close')
    }
  }
}
</script>
